export const useCourseData = () => {
  const {sanitize} = useUtils();
  const transformCatalog = (response) => {   
    const responseData = response?.data || {};
    const attributes = responseData?.attributes || {};
    const data = {
      id: responseData?.id ?? null,
      body: attributes.body,
      onCampusFilters: attributes.onCampusFilters,
      onlineFilters: attributes.onlineFilters,
      heroHeader: sanitize(attributes.heroHeader),
      noResultsAvailable: attributes.noResultsAvailable,
      jumpToTop: attributes.jumpToTop,
      displayHero: attributes.displayHero,
      brochureRibbon: attributes.brochureRibbon,
      brochureRibbonTags: attributes.tagsArray,
      seo: sanitize(attributes.seo),
      showChatService: attributes.showChatService,
      openNewTab: attributes.openNewTab,
      onCampusExperience: attributes.onCampusExperience,
    }
    return data;
  }

  const transformCatalogGrid = (response) => {   
    const responseData = response?.data || {};
    const attributes = responseData?.attributes || {};
    const data = {
      id: responseData?.id ?? null,
      heroHeader: sanitize(attributes.heroHeader),
      seo: sanitize(attributes.seo),
      ...attributes,
    }
    return data;
  }

  const tranformFloatingBlocks = (response) => { 
    const attributes = response?.data?.attributes || {};
    const data = {
      ...attributes,
      image: attributes?.image?.data?.attributes ?? null
    }
    return data;
  }

  const getFBPricesDates = async () => {        
    const url = `/api/fb-prices-dates`;

    return await useFetch(url, {
      method: 'GET',
      transform: tranformFloatingBlocks 
    });
  }

  const getFBSelectPackage = async () => {        
    const url = `/api/fb-select-package`;

    return await useFetch(url, {
      method: 'GET',
      transform: ((response) => {
        return {
          ...response.data.attributes
        }
      }) 
    });
  }

  const getFBSelectProgram = async () => {        
    const url = `/api/fb-select-program`;
    
    return await useFetch(url, {
      method: 'GET',
      transform: ((response) => {
        return {
          ...response.data.attributes
        }
      }) 
    });
  }

  const getFBGeolocationLookup = async () => {        
    const url = `/api/fb-geolocation-lookup`;
    
    return await useFetch(url, {
      method: 'GET',
      transform: ((response) => {
        return {
          ...response.data.attributes
        }
      }) 
    });
  }

  const getFBLocationSearch = async () => {        
    const url = `/api/fb-location-search`;
    
    return await useFetch(url, {
      method: 'GET',
      transform: tranformFloatingBlocks 
    });
  }

  const getFBViewLocations = async () => {        
    const url = `/api/fb-view-locations`;
    
    return await useFetch(url, {
      method: 'GET',
      transform: tranformFloatingBlocks
    });
  }

  const transformCourse = (response) => { 
    const attributes = response?.data?.attributes || {};
    const data = {
      ...attributes,
      blocks: sanitize((attributes.blocks || []).map((element) => {
        const result = {
          ...element,
          component: element.__component,
        }
        delete result.__component;
        return result;
      })), 
      pulseCourse: attributes.pulseCourse !== null && attributes?.pulseCourse?.data ? 
      {
        ...attributes.pulseCourse.data.attributes,
        id: attributes.pulseCourse.data.id,

      } : null,
      carousel: attributes.carousel,
      price: attributes.price,
      aboutCourseTitle: attributes.aboutCourseTitle,
      topicsCoveredTitle: attributes.topicsCoveredTitle,
      brochureRibbon: attributes.brochureRibbon,
      brochureRibbonTags: attributes.tagsArray,
      jumpToTop: attributes.jumpToTop,
      seo: sanitize(attributes.seo),
      publishedAt: sanitize(attributes.publishedAt),
      showChatService: attributes.showChatService
    }
    return data;
  }

  const transformLeadGen = (response) => {
    const attributes = response?.data?.attributes || {};
    const result = {
      ...attributes
    }
    return result;
  }

  const transform = (response) => {
    const data = (response?.data || []).map((element) => {
      const attributes = element?.attributes || {};
      return {
        id: element?.id ?? null,
        title: attributes.title,
        slug: attributes.slug,
        showOnCatalog: attributes.showOnCatalog,
        pulseId: attributes.pulseId,
        image: sanitize(attributes.image),
        pulseCourse: attributes.pulseCourse,
        isVirtual: attributes.isVirtual,
        isOnlinePrivateLesson: attributes.isOnlinePrivateLesson,
        isVTC: attributes.isVTC,
        isVTA: attributes.isVTA,
        isTeenAcademies3Week: attributes.isTeenAcademies3Week,
        isBootcamp: attributes.isBootcamp,
        isOnlineCamps: attributes.isOnlineCamps,
        isWeeklyClasses: attributes.isWeeklyClasses,
        isTechCamps: attributes.isTechCamps,
        isTechAcademies: attributes.isTechAcademies, 
        courseType: attributes.courseType,
        variants: attributes.variants,
        ...attributes
      }
    })      
    return {
      data: data,        
      meta: response.meta
    }
  }

  /**
   * Catalog grid “new” flag (data contract for /courses grid).
   *
   * `getCoursesGrid()` loads from Strapi `course-catalog-grid/courses`, which uses
   * `filterCoursesGrid()` and populates `pulseCourse` (`api::pulse-course.pulse-course`).
   * Pulse exposes this as a boolean on the product; Strapi stores it as `isNew`.
   *
   * Only `pulseCourse.isNew === true` is treated as new; anything else (false, null,
   * undefined, or missing `pulseCourse`) is non-new.
   *
   * Session vs full reload (for a future “New first” filter): default the UI to “All”
   * on full page load or refresh; use `useState` (or similar) if the selection should
   * persist across client-side navigations until the user changes it.
   */
  const isCourseNew = (course) => {
    return course?.pulseCourse?.isNew === true
  }

  const getCourseCatalog = async () => {        
    const url = `/api/course-catalog`;
    
    return await useFetch(url, {
      method: 'GET',
      transform: transformCatalog
    });
  }

  const getCourses = async () => {
    const url = `/api/courses`; 

    return await useFetch(url, {
      method: 'GET',
      transform: transform
    });
  }

  const courseLeadGen = async (_deep = true) => {
    const url = `/api/course-floating-block-lead-gen`

    let res = await useFetch(url, {
      method: 'GET',
      transform: transformLeadGen
    })
    return res.data
  }

  const getCourseBySlug = async (slug) => {    
    const {findOneBySlug} = useStrapiAsyncData();
    const url = 'courses';
    const params = {
      slug: slug,
      entity: url
    }
    return findOneBySlug(url, params, transformCourse);
  }

  const getCourseById = async (id) => {    
    const url = `/api/courses?id=${id}`; 

    return await useFetch(url, {
      method: 'GET',
      transform: transformCourse
    });
  }

  const getPulseCourseById = async (id) => {    
    const url = `/api/pulse-course?id=${id}`; 

    return await useFetch(url, {
      method: 'GET',
    });
  }

  const genres = (course) => {
    const result = course.genres.map(g => g.name)
    return result.join(', ')
  }
  
  const ages = (course) => {  
    return `Ages ${course.minAge}-${course.maxAge}`
  }
  
  const skillFullNames = (skillId) => {
    const fullNames = {
      51: 'Beginner',
      52: 'Intermediate',
      53: 'Advanced'
    }
  
    return fullNames[skillId] ? fullNames[skillId] : 'N/A';
  
  }
  
  const skillLevels = (course, abbrv = false) => {
    if (course.skillLevels.length == 0) {
      return '';
    }
      
    const sorted = course.skillLevels.sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });    
    
    if (sorted.length == 1) {
      return skillFullNames(sorted[0].id);
    }

    const levels = `${skillFullNames(sorted[0].id)}-${skillFullNames(sorted[1].id)}`;

    if (abbrv) {      
      return levels.replace('Beginner', 'Beg').replace('Intermediate', 'Int').replace('Advanced', 'Adv')
    }
    
    return levels;
  }
  
  const duration = (course) => {
    if (course.experiences.length == 0) {
      return '';
    }
  
    return course.experiences[0].shortName;
  }

  const format = (course) => {
    if (course.experiences.length == 0) {
      return '';
    }
    return course.experiences[0].name;
  }
  
  const getCourseCatalogGrid = async (_id) => {    
    const url = `/api/course-catalog-grid`; 

    return await useFetch(url, {
      method: 'GET',
      transform: transformCatalogGrid
    });
  }

  const getCoursesGrid = async () => {
    const url = `/api/courses-grid`; 

    return await useFetch(url, {
      method: 'GET',
      transform: transform
    });
  }

  const manageFilters = (items, selected = ['All']) => {
    const options = []
    items.forEach(element => {
      if (element === 'All (default)') {
        options.push({ value: 'All', label: 'All', checked: selected.includes('All')})
      }
      else {
        options.push({ value: element, label: element, checked: selected.includes(element)})
      } 
    });

    return options;
  }

  const getAgeRange = (age, filter = null) => {
    if (filter === null) {
      return age <= 9 ? ['7-9'] : age <= 12 ? ['10-12'] : age <= 19 ? ['13-19'] : ['All']
    }
    else {
      const result = filter.find((range) => {      
        const split = range.split('-');
        const min = parseInt(split[0]), max = parseInt(split[1]);
        return parseInt(age) >= min && parseInt(age) <= max; 
      })
      return result;
    }
    
  }

  const setupGridFilters = (filterType, filter) => {
    if (filterType === 'ages' && filter && filter.display) {
      const selected = filter.selected && filter.selected.length > 0 ? [getAgeRange(filter.selected, filter.options)] : ['All'];      
      const gridFilter = {
        id: filter.id,
        name: filter.name,
        sortOrder: filter.sortOrder,
        inputName: 'ages',
        singleSelect: false,
        selected: selected,
        options: [
          {value: 'All', label: 'All', checked: selected === 'All'},
          {value: '7-9', label: '7-9', checked: selected === '7-9'},
          {value: '10-12', label: '10-12', checked: selected === '10-12'},
          {value: '13-19', label: '13-19', checked: selected === '13-19'},
        ]
      }      
      // In case ages are being defined in Strapi we will get it from there otherwise we keep the default values
      if (filter.options.length > 0) {
        gridFilter.options = manageFilters(filter.options, selected)
      }
    
      return gridFilter;
    
    }

    if (filterType === 'interests' && filter && filter.display) {
      const selected = filter.selected && filter.selected.length > 0 ? filter.selected : ['All'];
      const gridFilter = {
        id: filter.id,
        name: filter.name,
        sortOrder: filter.sortOrder,
        inputName: 'interests',
        singleSelect: false,
        selected: selected,
        options: [
          {value: 'All', label: 'All', checked: true},
          {value: 'Coding', label: 'Coding'},
          {value: 'Artificial Intelligence', label: 'Artificial Intelligence'},
          {value: 'Game Dev', label: 'Game Dev'},
          {value: 'Robotics', label: 'Robotics'},
          {value: 'Creative', label: 'Creative'},
          {value: 'Math', label: 'Math'},
          {value: '3D Printing', label: '3D Printing'},
        ]
      }      
      // In case ages are being defined in Strapi we will get it from there otherwise we keep the default values
      if (filter.options.length > 0) {
        gridFilter.options = manageFilters(filter.options, selected)
      }
    
      return gridFilter;
    
    }

    if (filterType === 'programs' && filter && filter.display) {
      const selected = filter.selected && filter.selected.length > 0 ? filter.selected : ['All'];
      const gridFilter = {
        id: filter.id,
        name: filter.name,
        sortOrder: filter.sortOrder,
        inputName: 'programs',
        singleSelect: false,
        selected: selected,
        options: [
          {value: 'All', label: 'All', checked: true},
          {value: 'Tech Camps', label: 'Tech Camps'},
          {value: 'Teen Academies', label: 'Teen Academies'},
          {value: 'Private Lessons', label: 'Private Lessons'},
        ]
      }
    
      // In case ages are being defined in Strapi we will get it from there otherwise we keep the default values
      if (filter.options.length > 0) {
        gridFilter.options = manageFilters(filter.options, selected)
      }
    
      return gridFilter;
    
    }

    if (filterType === 'format' && filter && filter.display) {
      const selected = filter.selected && filter.selected.length > 0 ? filter.selected : ['All'];
      const gridFilter = {
        id: filter.id,
        name: filter.name,
        sortOrder: filter.sortOrder,
        inputName: 'format',
        singleSelect: true,
        selected: selected,
        options: [
          {value: 'All', label: 'All', checked: true},
          {value: 'On-Campus', label: 'On-Campus'},
          {value: 'Online', label: 'Online'},
        ]
      }
    
      // In case ages are being defined in Strapi we will get it from there otherwise we keep the default values
      if (filter.options.length > 0) {
        gridFilter.options = manageFilters(filter.options, selected)
      }
    
      return gridFilter;
    
    }
  }

  const manageMobileFilters = (items, type = 'normal') => {
    const options = []
    items.forEach(element => {
      if (element === 'All (default)') {
        options.push('All')
      }
      else {
        if (type === 'ages') {
          options.push(`Ages ${element}`)
        }
        else {
          options.push(element)
        }
      } 
    });

    return options;
  }  

  const setupMobileGridFilters = (filterType, filter) => {
    if (filterType === 'ages' && filter && filter.display) {
      let selected = ['All'];
      if (filter.selected && filter.selected.length > 0 && filter.selected[0] !== 'All') {
        const range = filter.selected[0].replace('-', ' - ')
        selected = [`Ages ${range}`];
      }
      
      const gridFilter = {
        id: `ages-${filter.sortOrder}`,
        title: filter.name,
        type: 'ages',
        all: 'All',
        value: selected,        
        order: filter.sortOrder,        
        options: [
          'All',
          'Ages 7-9',
          'Ages 10-12',
          'Ages 13-19'
        ]
      }
      // In case ages are being defined in Strapi we will get it from there otherwise we keep the default values
      if (filter.options.length > 0) {
        gridFilter.options = manageMobileFilters(filter.options, 'ages')
      }
    
      return gridFilter;
    
    }

    if (filterType === 'interests' && filter && filter.display) {
      const selected = filter.selected && filter.selected.length > 0 ? filter.selected : ['All'];
      const gridFilter = {
        id: `interests-${filter.sortOrder}`,
        title: filter.name,
        type: 'interests',
        all: 'All',
        value: selected,        
        order: filter.sortOrder,
        options: [
          'All', 'Coding', 'Game Dev',  'Robotics',  'Creative',  'Business',  'Math'
        ]
      }
    
      // In case ages are being defined in Strapi we will get it from there otherwise we keep the default values
      if (filter.options.length > 0) {
        gridFilter.options = manageMobileFilters(filter.options)
      }
    
      return gridFilter;
    
    }

    if (filterType === 'programs' && filter && filter.display) {
      const selected = filter.selected && filter.selected.length > 0 ? filter.selected : ['All'];
      const gridFilter = {
        id: `programs-${filter.sortOrder}`,
        title: filter.name,
        type: 'programs',
        all: 'All',
        value: selected,        
        order: filter.sortOrder,
        options: [
          'All', 'Tech Camps', 'Teen Academies', 'Private Lessons'
        ]
      }
    
      // In case ages are being defined in Strapi we will get it from there otherwise we keep the default values
      if (filter.options.length > 0) {
        gridFilter.options = manageMobileFilters(filter.options)
      }
    
      return gridFilter;
    
    }

    if (filterType === 'format' && filter && filter.display) {
      const selected = filter.selected && filter.selected.length > 0 ? filter.selected : ['All'];
      const gridFilter = {
        id: `format-${filter.sortOrder}`,
        title: filter.name,
        type: 'format',
        all: 'All',
        value: selected,        
        order: filter.sortOrder,
        options: [
          'All', 'On-Campus', 'Online'
        ]
      }
    
      // In case ages are being defined in Strapi we will get it from there otherwise we keep the default values
      if (filter.options.length > 0) {
        gridFilter.options = manageMobileFilters(filter.options)
      }
    
      return gridFilter;
    
    }
  }
  

  return {
    getCourseCatalog,
    getCourses,
    getCourseBySlug,
    genres,
    ages,
    skillLevels,
    duration,
    format,
    getFBLocationSearch,
    getFBViewLocations,
    courseLeadGen,   
    getFBPricesDates,
    getFBSelectPackage,
    getFBSelectProgram,
    getCourseById,
    getPulseCourseById,
    getCourseCatalogGrid,
    getCoursesGrid,
    setupGridFilters,
    setupMobileGridFilters,
    getAgeRange,
    getFBGeolocationLookup,
    isCourseNew
  }
}

